import { useCallback, useState } from 'react';

import { useStorageToken } from '~lib/auth/use-storage-token';
import { downloadBlob } from '~lib/storage/download-blob';

interface UseDownloadBlobResult {
  downloading: string | null;
  // Bytes received so far for the in-flight blob. Resets to 0 on each call.
  bytesDownloaded: number;
  // Total bytes from the server's Content-Length, or the size hint passed by
  // the caller. `null` when neither is available — UI should fall back to an
  // indeterminate state in that case.
  totalBytes: number | null;
  error: Error | null;
  // `knownSize` lets callers prime the totalBytes from their already-loaded
  // dataset metadata so the UI ring starts at a sensible position even before
  // the first chunk arrives.
  download: (fullPath: string, knownSize?: number) => Promise<void>;
}

// Wraps downloadBlob with token acquisition and tracks which blob is currently
// in flight so a row can show a progress ring without per-row hooks.
export function useDownloadBlob(): UseDownloadBlobResult {
  const getToken = useStorageToken();
  const [downloading, setDownloading] = useState<string | null>(null);
  const [bytesDownloaded, setBytesDownloaded] = useState(0);
  const [totalBytes, setTotalBytes] = useState<number | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const download = useCallback(
    async (fullPath: string, knownSize?: number) => {
      setDownloading(fullPath);
      setBytesDownloaded(0);
      setTotalBytes(knownSize ?? null);
      setError(null);
      try {
        const token = await getToken();
        await downloadBlob(token, fullPath, {
          onProgress: (bytes, total) => {
            setBytesDownloaded(bytes);
            // Prefer the server's Content-Length if it shows up — it's the
            // ground truth for what's coming over the wire. Fall back to the
            // caller's hint when the header is missing.
            if (total != null) setTotalBytes(total);
          },
        });
      } catch (e) {
        setError(e instanceof Error ? e : new Error(String(e)));
      } finally {
        setDownloading(null);
      }
    },
    [getToken],
  );

  return { downloading, bytesDownloaded, totalBytes, error, download };
}
